import { FC, useMemo } from 'react';
import classNames from 'classnames';
import dynamic from 'next/dynamic';
import { useTranslation } from 'next-i18next';

import type { UserDetails, FullBoatSearchDetail } from 'src/common/types/api/v4/boats/search';
import useInView from 'src/common/hooks/useInView';
import getYearOfBestOfAward from 'src/common/utils/getYearOfBestOfAward';
import { usePreviewModeContext } from 'src/common/providers/PreviewModeProvider';
import Avatar from 'src/common/components/Avatar';
import getClassNames from 'src/common/utils/getClassNames';
import LoadingSpinner from 'src/common/components/LoadingSpinner';
import BookingCounter from '../BookingCounter';
import Badges from '../Badges';

import styles from './BoatOwnerInfo.module.scss';

export type BoatOwnerInfoProps = {
  boatOwner: UserDetails;
  withDescription?: boolean;
  classNameModifier?: string;
  ownerBoats?: FullBoatSearchDetail[];
};

// Owner Other Listings
const ownerOtherListingsLoader = () => import('../OwnerOtherListings');
const OwnerOtherListings = dynamic(ownerOtherListingsLoader, {
  loading: () => <div className={styles.loading}><LoadingSpinner variant="l" /></div>,
  ssr: false,
});

const BoatOwnerInfo: FC<BoatOwnerInfoProps> = ({
  boatOwner,
  withDescription = false,
  classNameModifier,
  ownerBoats,
}) => {
  const [t] = useTranslation('listing-page');
  const { previewMode } = usePreviewModeContext();
  const { description, awards, total_successful_bookings: totalBookings } = boatOwner;
  const bestOfYear = useMemo(() => getYearOfBestOfAward(awards), [awards]);

  const modifier = classNames(classNameModifier, {
    withDescription: withDescription && description,
    withBadge: !!bestOfYear,
  });

  // Owner Other Boats
  const { ref: intersectionObserverRef, inView } = useInView<HTMLDivElement>({
    threshold: 0,
    trackOnce: true,
  });
  const hasOtherListings = ownerBoats && ownerBoats.length > 0;
  return (
    <div className={getClassNames(styles, 'root', modifier)}>
      <div className={getClassNames(styles, 'wrapper', modifier)}>
        <div className={getClassNames(styles, 'avatar', modifier)}>
          <Avatar
            user={boatOwner}
            size="medium"
            classNameModifier="noMargin"
          />
        </div>
        <div className={getClassNames(styles, 'content', modifier)}>
          <h2 className={getClassNames(styles, 'name', modifier)}>{boatOwner.first_name}</h2>
          {(previewMode || totalBookings > 0) ? (
            <BookingCounter
              classNameModifier="boatOwnerInfo"
              totalBookings={totalBookings}
            />
          ) : (
            <span className={styles.label}>{t('boat-owner-info--owner')}</span>
          )}
          <div className={styles.badgesWrapper}>
            <Badges
              isSuperowner={boatOwner.is_super_owner}
              bestOfYear={bestOfYear}
            />
          </div>
        </div>
        {withDescription && description && <p className={styles.description}>{description}</p>}
      </div>
      {hasOtherListings && (
        <div
          ref={intersectionObserverRef}
          className={styles.ownerOtherListings}
        >
          {inView && (
            <OwnerOtherListings
              ownerName={boatOwner.first_name}
              ownerBoats={ownerBoats}
            />
          )}
        </div>
      )}
    </div>
  );
};

export default BoatOwnerInfo;
